import React, { useEffect, useState } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import { useClientModules } from '../contexts/ClientModulesContext';
import { api } from '../utils/api';
import { sanitizeHtml } from '../utils/sanitizeHtml';

// Single viewer page that handles both content_pdf and content_html modules.
// Built-in modules have their own dedicated pages (DemographicPage, etc.) and
// don't route here — if a built-in id somehow lands at /modules/:id we fall
// back to "module not found" because it's not what this page exists for.
//
// PDF rendering: the bytes are downloaded via api.getMyModulePdfBlob and
// rendered through a blob object URL in an <iframe>. We can't put the API
// URL directly in src= because the iframe can't carry the auth header.
//
// HTML rendering: html_content arrives inlined in the /api/modules/me
// response, so no extra fetch needed. It's run through sanitizeHtml before
// being injected to defend against any unsafe HTML an admin might have
// pasted into the rich-text editor.

export default function ContentModulePage() {
  const { id } = useParams();
  const { modules, loading } = useClientModules();
  const [pdfUrl, setPdfUrl]     = useState(null);
  const [pdfError, setPdfError] = useState('');

  const module = modules.find(m => m.id === id);

  useEffect(() => {
    if (!module || module.module_type !== 'content_pdf') {
      setPdfUrl(null);
      return undefined;
    }
    let revoked = false;
    let currentUrl = null;
    setPdfError('');
    api.getMyModulePdfBlob(id)
      .then(blob => {
        if (revoked) return;
        currentUrl = URL.createObjectURL(blob);
        setPdfUrl(currentUrl);
      })
      .catch(err => {
        if (!revoked) setPdfError(err.message || 'Could not load PDF.');
      });
    return () => {
      revoked = true;
      if (currentUrl) URL.revokeObjectURL(currentUrl);
    };
  }, [module, id]);

  if (loading) {
    return (
      <div style={{ padding: 40, fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading...</div>
    );
  }

  if (!module) {
    // Not assigned, not active, or wrong id — bounce back to dashboard.
    return <Navigate to="/" replace />;
  }

  // Built-in modules have dedicated pages and shouldn't render here.
  if (module.module_type === 'builtin') {
    return <Navigate to={module.route || '/'} replace />;
  }

  return (
    <div>
      <h1 className="page-title">{module.label}</h1>
      {module.description && (
        <p className="page-subtitle">{module.description}</p>
      )}

      {module.module_type === 'content_pdf' && (
        <>
          {pdfError && <div className="alert alert-error">{pdfError}</div>}
          {!pdfUrl && !pdfError && (
            <div style={{ fontFamily: 'Arial, sans-serif', color: '#888' }}>Loading document...</div>
          )}
          {pdfUrl && (
            <iframe
              src={pdfUrl}
              title={module.label}
              style={{
                width: '100%',
                height: 'calc(100vh - 220px)',
                minHeight: 500,
                border: '1px solid #ddd',
                borderRadius: 4,
                background: '#fff',
              }}
            />
          )}
        </>
      )}

      {module.module_type === 'content_html' && (
        <div
          className="card"
          // sanitizeHtml strips scripts, event handlers, unsafe URL schemes,
          // and any tags outside a small safe allow-list. See utils/sanitizeHtml.
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(module.html_content || '') }}
        />
      )}
    </div>
  );
}
